<template>
  <div class="page-container">
    <div class="settings-form">
      <!-- 顶部引导区域 -->
      <div class="guide-section">
        <div class="guide-content">
          <h2 class="guide-title">
            <el-icon class="guide-icon">
              <Message />
            </el-icon>
            短信配置管理
          </h2>
          <p class="guide-desc">配置短信服务，支持阿里云和腾讯云短信服务商</p>
        </div>
        <div class="guide-actions">
          <el-button type="primary" @click="fetchSettings" :loading="loading.refresh">
            <el-icon>
              <Refresh />
            </el-icon>
            刷新配置
          </el-button>
        </div>
      </div>

      <!-- 短信配置表单 -->
      <el-card class="sms-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <div class="card-title-section">
              <el-icon class="card-icon">
                <Setting />
              </el-icon>
              <span class="card-title">短信服务配置</span>
              <el-tag v-if="smsForm.is_enabled" type="success" size="small">已启用</el-tag>
              <el-tag v-else type="info" size="small">未启用</el-tag>
            </div>
            <el-button type="primary" @click="saveSettings" :loading="loading.save" class="save-btn">
              <el-icon>
                <Check />
              </el-icon>
              保存配置
            </el-button>
          </div>
        </template>

        <div class="form-content">
          <!-- 服务商选择 -->
          <div class="provider-tabs">
            <el-tabs v-model="activeProvider" @tab-change="handleProviderChange">
              <el-tab-pane name="aliyun">
                <template #label>
                  <span class="tab-label">
                    <el-icon><Aim /></el-icon>
                    阿里云短信
                  </span>
                </template>
              </el-tab-pane>
              <el-tab-pane name="tencent">
                <template #label>
                  <span class="tab-label">
                    <el-icon><ChatDotRound /></el-icon>
                    腾讯云短信
                  </span>
                </template>
              </el-tab-pane>
            </el-tabs>
          </div>

          <!-- 其余代码保持不变 -->
          <el-form :model="smsForm" :rules="formRules" ref="smsFormRef" label-width="140px">
            <!-- 启用开关 -->
            <el-form-item label="启用短信服务">
              <div class="switch-container">
                <el-switch v-model="smsForm.is_enabled" :active-value="1" :inactive-value="0" active-color="#13ce66"
                  @change="handleEnableChange" />
                <span class="switch-label" :class="{ active: smsForm.is_enabled }">
                  {{ smsForm.is_enabled ? '服务已启用' : '服务已禁用' }}
                </span>
              </div>
              <div class="form-tip">启用后系统将使用短信服务发送验证码等消息</div>
            </el-form-item>

            <!-- 阿里云配置 -->
            <div v-if="activeProvider === 'aliyun'">
              <el-divider content-position="left">阿里云 AccessKey 配置</el-divider>

              <el-form-item label="AccessKey ID" prop="access_key_id">
                <el-input v-model="smsForm.access_key_id" placeholder="请输入阿里云AccessKey ID" clearable
                  :disabled="!smsForm.is_enabled" />
                <div class="form-tip">在阿里云控制台创建的AccessKey ID</div>
              </el-form-item>

              <el-form-item label="AccessKey Secret" prop="access_key_secret">
                <el-input v-model="smsForm.access_key_secret" placeholder="请输入阿里云AccessKey Secret" show-password
                  clearable :disabled="!smsForm.is_enabled" />
                <div class="form-tip">与AccessKey ID对应的Secret密钥</div>
              </el-form-item>

              <!-- 短信配置 -->
              <el-divider content-position="left">阿里云短信配置</el-divider>

              <el-form-item label="短信签名" prop="sign_name">
                <el-input v-model="smsForm.sign_name" placeholder="请输入短信签名" clearable :disabled="!smsForm.is_enabled" />
                <div class="form-tip">在阿里云审核通过的短信签名，如：矩阵怪</div>
              </el-form-item>

              <el-form-item label="模板CODE" prop="template_code">
                <el-input v-model="smsForm.template_code" placeholder="请输入短信模板CODE" clearable
                  :disabled="!smsForm.is_enabled" />
                <div class="form-tip">在阿里云审核通过的短信模板CODE，如：SMS_217946419</div>
              </el-form-item>

              <el-form-item label="服务端点">
                <el-input v-model="smsForm.endpoint" placeholder="请输入短信服务端点" clearable
                  :disabled="!smsForm.is_enabled" />
                <div class="form-tip">阿里云短信服务端点，默认：dysmsapi.aliyuncs.com</div>
              </el-form-item>
            </div>

            <!-- 腾讯云配置 -->
            <div v-if="activeProvider === 'tencent'">
              <el-divider content-position="left">腾讯云密钥配置</el-divider>

              <el-form-item label="SecretId" prop="tencent_secret_id">
                <el-input v-model="smsForm.tencent_secret_id" placeholder="请输入腾讯云SecretId" clearable
                  :disabled="!smsForm.is_enabled" />
                <div class="form-tip">在腾讯云控制台创建的SecretId</div>
              </el-form-item>

              <el-form-item label="SecretKey" prop="tencent_secret_key">
                <el-input v-model="smsForm.tencent_secret_key" placeholder="请输入腾讯云SecretKey" show-password clearable
                  :disabled="!smsForm.is_enabled" />
                <div class="form-tip">与SecretId对应的SecretKey</div>
              </el-form-item>

              <el-divider content-position="left">腾讯云短信配置</el-divider>

              <el-form-item label="应用ID" prop="tencent_app_id">
                <el-input v-model="smsForm.tencent_app_id" placeholder="请输入短信应用AppId" clearable
                  :disabled="!smsForm.is_enabled" />
                <div class="form-tip">在腾讯云短信控制台创建的应用ID</div>
              </el-form-item>

              <el-form-item label="短信签名" prop="tencent_sign_name">
                <el-input v-model="smsForm.tencent_sign_name" placeholder="请输入短信签名" clearable
                  :disabled="!smsForm.is_enabled" />
                <div class="form-tip">在腾讯云审核通过的短信签名</div>
              </el-form-item>

              <el-form-item label="模板ID" prop="tencent_template_id">
                <el-input v-model="smsForm.tencent_template_id" placeholder="请输入短信模板ID" clearable
                  :disabled="!smsForm.is_enabled" />
                <div class="form-tip">在腾讯云审核通过的短信模板ID</div>
              </el-form-item>

              <el-form-item label="服务地域">
                <el-input v-model="smsForm.tencent_region" placeholder="请输入服务地域" clearable
                  :disabled="!smsForm.is_enabled" />
                <div class="form-tip">腾讯云短信服务地域，默认：ap-guangzhou</div>
              </el-form-item>
            </div>

            <!-- 测试区域 -->
            <el-divider content-position="left">功能测试</el-divider>

            <el-form-item label="测试手机号">
              <div class="test-section">
                <el-input v-model="testPhone" placeholder="请输入测试手机号" style="width: 200px; margin-right: 12px;"
                  :disabled="!smsForm.is_enabled" />
                <el-button type="success" @click="testSms" :loading="loading.test"
                  :disabled="!smsForm.is_enabled || !testPhone">
                  <el-icon>
                    <Promotion />
                  </el-icon>
                  发送测试短信
                </el-button>
                <div class="form-tip">输入手机号测试短信服务是否配置正确</div>
              </div>
            </el-form-item>

            <!-- 配置信息 -->
            <template v-if="smsForm.last_update_time">
              <el-divider content-position="left">配置信息</el-divider>

              <el-form-item label="最后更新">
                <div class="update-info">
                  <el-icon>
                    <Clock />
                  </el-icon>
                  <span class="update-time">{{ smsForm.last_update_time }}</span>
                </div>
              </el-form-item>

              <el-form-item label="创建时间" v-if="smsForm.create_time">
                <div class="update-info">
                  <el-icon>
                    <Calendar />
                  </el-icon>
                  <span class="update-time">{{ smsForm.create_time }}</span>
                </div>
              </el-form-item>
            </template>
          </el-form>
        </div>
      </el-card>

      <!-- 配置说明 -->
      <el-card class="info-card" shadow="hover">
        <template #header>
          <div class="card-header">
            <el-icon>
              <InfoFilled />
            </el-icon>
            <span class="card-title">配置说明</span>
          </div>
        </template>

        <div class="info-content">
          <div v-if="activeProvider === 'aliyun'">
            <h4>阿里云短信服务配置步骤：</h4>
            <ol class="steps-list">
              <li>登录阿里云控制台，进入短信服务</li>
              <li>申请短信签名（需企业资质审核）</li>
              <li>创建短信模板（验证码、通知等）</li>
              <li>获取 AccessKey ID 和 AccessKey Secret</li>
              <li>将以上信息填写到本页面并保存</li>
              <li>使用测试功能验证配置是否正确</li>
            </ol>
          </div>

          <div v-if="activeProvider === 'tencent'">
            <h4>腾讯云短信服务配置步骤：</h4>
            <ol class="steps-list">
              <li>登录腾讯云控制台，进入短信服务</li>
              <li>创建短信应用，获取 AppId</li>
              <li>申请短信签名（需企业资质审核）</li>
              <li>创建短信模板（验证码、通知等）</li>
              <li>获取 SecretId 和 SecretKey</li>
              <li>将以上信息填写到本页面并保存</li>
              <li>使用测试功能验证配置是否正确</li>
            </ol>
          </div>

          <div class="notice">
            <el-alert title="注意事项" type="warning" :closable="false">
              <ul>
                <li>AccessKey Secret 和 SecretKey 是敏感信息，请妥善保管</li>
                <li>短信签名和模板需要服务商审核通过后才能使用</li>
                <li>测试前请确保短信服务余额充足</li>
                <li>正式环境建议使用 RAM 子账号的 AccessKey</li>
                <li>切换服务商后请重新保存配置</li>
              </ul>
            </el-alert>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { ElMessage } from 'element-plus';
import {
  Message, Refresh, Setting, Check, Promotion, Clock, Calendar, InfoFilled,
  Aim, ChatDotRound
} from '@element-plus/icons-vue';

export default {
  name: 'SmsManagement',
  components: {
    Message, Refresh, Setting, Check, Promotion, Clock, Calendar, InfoFilled,
    Aim, ChatDotRound
  },
  data() {
    return {
      activeProvider: 'aliyun',
      loading: {
        save: false,
        test: false,
        refresh: false
      },
      smsForm: {
        provider: 'aliyun',
        access_key_id: '',
        access_key_secret: '',
        sign_name: '',
        template_code: '',
        endpoint: 'dysmsapi.aliyuncs.com',
        tencent_secret_id: '',
        tencent_secret_key: '',
        tencent_app_id: '',
        tencent_sign_name: '',
        tencent_template_id: '',
        tencent_region: 'ap-guangzhou',
        is_enabled: 0,
        last_update_time: null,
        create_time: null
      },
      testPhone: '',
      formRules: {
        access_key_id: [
          { required: true, message: '请输入AccessKey ID', trigger: 'blur' }
        ],
        access_key_secret: [
          { required: true, message: '请输入AccessKey Secret', trigger: 'blur' }
        ],
        sign_name: [
          { required: true, message: '请输入短信签名', trigger: 'blur' }
        ],
        template_code: [
          { required: true, message: '请输入模板CODE', trigger: 'blur' }
        ],
        tencent_secret_id: [
          { required: true, message: '请输入SecretId', trigger: 'blur' }
        ],
        tencent_secret_key: [
          { required: true, message: '请输入SecretKey', trigger: 'blur' }
        ],
        tencent_app_id: [
          { required: true, message: '请输入应用ID', trigger: 'blur' }
        ],
        tencent_sign_name: [
          { required: true, message: '请输入短信签名', trigger: 'blur' }
        ],
        tencent_template_id: [
          { required: true, message: '请输入模板ID', trigger: 'blur' }
        ]
      }
    };
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}');
    }
  },
  mounted() {
    this.fetchSettings();
  },
  methods: {
    // 获取短信配置
    async fetchSettings() {
      this.loading.refresh = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/sms_management.php`,
          {
            action: 'get_settings',
            store_id: this.userInfo.id
          }
        );

        if (response.data.data) {
          this.smsForm = { ...this.smsForm, ...response.data.data };
          this.activeProvider = this.smsForm.provider || 'aliyun';
          ElMessage.success('配置加载成功');
        } else {
          // 没有配置时重置表单
          this.resetForm();
          ElMessage.info('暂无短信配置，请进行配置');
        }
      } catch (error) {
        ElMessage.error('获取短信配置失败');
        console.error('获取短信配置错误:', error);
      } finally {
        this.loading.refresh = false;
      }
    },

    // 保存配置
    async saveSettings() {
      this.$refs.smsFormRef.validate(async (valid) => {
        if (!valid) {
          ElMessage.warning('请完善表单信息');
          return;
        }

        this.loading.save = true;
        try {
          const formData = {
            ...this.smsForm,
            provider: this.activeProvider
          };

          const response = await axios.post(
            `${config.BASE_URL}/shop/api/sms_management.php`,
            {
              action: 'save_settings',
              store_id: this.userInfo.id,
              ...formData
            }
          );

          if (response.data.success) {
            ElMessage.success('短信配置保存成功');
            this.fetchSettings(); // 重新加载配置
          } else {
            ElMessage.error(response.data.error || '保存失败');
          }
        } catch (error) {
          ElMessage.error('保存短信配置失败');
          console.error('保存短信配置错误:', error);
        } finally {
          this.loading.save = false;
        }
      });
    },

    // 测试短信发送
    async testSms() {
      if (!this.testPhone) {
        ElMessage.warning('请输入测试手机号');
        return;
      }

      if (!/^1[3-9]\d{9}$/.test(this.testPhone)) {
        ElMessage.warning('请输入正确的手机号码');
        return;
      }

      this.loading.test = true;
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/sms_management.php`,
          {
            action: 'test_sms',
            store_id: this.userInfo.id,
            phone_number: this.testPhone
          }
        );

        if (response.data.success) {
          ElMessage.success('测试短信发送成功');
        } else {
          ElMessage.error(response.data.error || '测试短信发送失败');
        }
      } catch (error) {
        ElMessage.error('测试短信发送失败');
        console.error('测试短信发送错误:', error);
      } finally {
        this.loading.test = false;
      }
    },

    // 处理服务商切换
    handleProviderChange(provider) {
      this.activeProvider = provider;
      // 重置验证
      this.$nextTick(() => {
        this.$refs.smsFormRef.clearValidate();
      });
    },

    // 处理启用状态变化
    handleEnableChange(enabled) {
      if (enabled) {
        ElMessage.info('已启用短信服务');
      } else {
        ElMessage.warning('已禁用短信服务');
      }
    },

    // 重置表单
    resetForm() {
      this.smsForm = {
        provider: 'aliyun',
        access_key_id: '',
        access_key_secret: '',
        sign_name: '',
        template_code: '',
        endpoint: 'dysmsapi.aliyuncs.com',
        tencent_secret_id: '',
        tencent_secret_key: '',
        tencent_app_id: '',
        tencent_sign_name: '',
        tencent_template_id: '',
        tencent_region: 'ap-guangzhou',
        is_enabled: 0,
        last_update_time: null,
        create_time: null
      };
      this.testPhone = '';
      this.activeProvider = 'aliyun';
    }
  }
};
</script>

<style scoped lang="scss">
.page-container {
  padding: 20px;
  min-height: 100vh;
}

.settings-form {
  max-width: 100%;
  margin: 0 auto;
}

/* 引导区域 */
.guide-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 24px;
  background: linear-gradient(135deg, #67c23a 0%, #529b2e 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 12px rgba(103, 194, 58, 0.2);
}

.guide-content {
  .guide-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 8px;

    .guide-icon {
      font-size: 28px;
    }
  }

  .guide-desc {
    font-size: 14px;
    opacity: 0.9;
    margin: 0;
  }
}

/* 卡片样式 */
.sms-card,
.info-card {
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid #ebeef5;
  background: #ffffff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
  background: #f8fafc;
  border-radius: 12px 12px 0 0;
}

.card-title-section {
  display: flex;
  align-items: center;
  gap: 12px;

  .card-icon {
    font-size: 20px;
    color: #67c23a;
  }

  .card-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }
}

.save-btn {
  background: linear-gradient(135deg, #67c23a 0%, #529b2e 100%);
  border: none;
  color: white;

  &:hover {
    background: linear-gradient(135deg, #529b2e 0%, #67c23a 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(103, 194, 58, 0.3);
  }
}

/* 服务商标签页 */
.provider-tabs {
  margin-bottom: 24px;

  :deep(.el-tabs__header) {
    margin-bottom: 0;
  }

  :deep(.el-tabs__item) {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
  }

  .tab-label {
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

/* 表单内容 */
.form-content {
  padding: 20px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #606266;
}

:deep(.el-divider__text) {
  background: #fff;
  color: #409eff;
  font-weight: 600;
}

/* 开关样式 */
.switch-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.switch-label {
  color: #909399;
  font-size: 14px;

  &.active {
    color: #67c23a;
    font-weight: 500;
  }
}

/* 表单提示 */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  line-height: 1.4;
}

/* 测试区域 */
.test-section {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

/* 更新信息 */
.update-info {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-icon {
    color: #909399;
  }

  .update-time {
    color: #909399;
    font-size: 14px;
  }
}

/* 配置说明 */
.info-content {
  h4 {
    color: #1f2937;
    margin-bottom: 16px;
  }

  .steps-list {
    margin-bottom: 20px;
    padding-left: 20px;

    li {
      margin-bottom: 8px;
      color: #606266;
      line-height: 1.6;
    }
  }

  .notice {
    margin-top: 20px;

    ul {
      margin: 8px 0 0 20px;

      li {
        margin-bottom: 4px;
        color: #606266;
        line-height: 1.5;
      }
    }
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .guide-section {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .card-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .test-section {
    flex-direction: column;
    align-items: flex-start;
  }

  :deep(.el-form-item) {
    margin-bottom: 16px;
  }

  .provider-tabs {
    :deep(.el-tabs__item) {
      padding: 0 12px;
      font-size: 14px;
    }
  }
}
</style>